'use client';

import React from 'react';
import { m, useReducedMotion } from 'framer-motion';
import { usePathname } from 'next/navigation';
import { appEntrance, entranceDur, entranceEase, entranceShift } from '@/components/motion/tokens';

/**
 * The per-navigation entrance.
 *
 * This is a Next `template.tsx` and not an AnimatePresence keyed on
 * `usePathname()`, which is what it was, and which was broken in a way worth
 * writing down:
 *
 * `usePathname()` flips to the new route the moment a client navigation starts,
 * but `children` keeps holding the OLD page's tree until its payload resolves.
 * A presence keyed on the pathname therefore tore down and rebuilt on the new
 * key while still holding the previous page's content — so the leaderboard
 * animated in showing the leaderboard, then the real children arrived and it
 * animated in again. That is the double-load. With `mode="wait"` on top of it,
 * the next navigation had to wait for an exit that was animating the wrong
 * subtree, and the home page could be left holding nothing at all.
 *
 * A template cannot desync, because it is not observing the route from outside
 * — it IS part of the route tree, and React remounts it together with the page
 * it wraps. One mount, one entrance, always with the right content.
 *
 * The cost is that there is no exit animation: React unmounts the old tree
 * immediately. A crossfade would need both trees alive at once, which is
 * exactly the thing that was going wrong. An enter-only fade is the honest
 * version of this.
 */
/**
 * Module scope, so it survives this template's remount on every navigation.
 * The one-time offset that lets the chrome arrive first is applied once per
 * session and never again — holding a third of a second before every page swap
 * would be a tax on ordinary use to pay for a moment that happens once.
 */
let chromeHasEntered = false;

export default function Template({ children }: { children: React.ReactNode }) {
  const path = usePathname();
  const reduce = useReducedMotion();
  // Read on mount, before the effect below can flip it. A ref rather than
  // state: nothing should re-render when this changes.
  const isFirstMount = React.useRef(!chromeHasEntered);
  React.useEffect(() => {
    chromeHasEntered = true;
  }, []);

  // OBS overlays and the admin shell get no transition: an overlay is composited
  // onto a broadcast and must never fade, and /admin drives its own.
  if (path?.startsWith('/obs') || path?.startsWith('/admin') || reduce) {
    return <>{children}</>;
  }

  // The travel is deliberately small next to the duration. A long slow SLIDE
  // reads as the page struggling to load; a long slow FADE with just enough
  // drift to have a direction reads as composure.
  //
  // No `filter` here. It is not a compositor-only property: animating it forces
  // the browser to rasterise this subtree — the entire page — into a texture and
  // re-blur it every frame.
  return (
    <m.div
      initial={{ opacity: 0, y: entranceShift.sm }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: entranceDur.page,
        ease: entranceEase.enter,
        delay: isFirstMount.current ? appEntrance.contentDelay : 0,
      }}
    >
      {children}
    </m.div>
  );
}
